import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useHasFF } from 'venn-utils';
import { StudioContext, useRegisterStudioActionInterceptor, type StudioActionName } from 'venn-components';
import {
  analysisSubjects,
  analysisViewIdState,
  isPrintingState,
  isReportState,
  multiSubjectInputGroupSubjects,
  subjectInputGroups,
  useCachedLoadableValue,
  workspaceFeeLimitsState,
} from 'venn-state';
import { AdvisoryFeeCorrectionModal } from './AdvisoryFeeCorrectionModal';
import type { FeeAdjustType, ModalMode, SubjectFee } from './types';
import {
  adjustSubjectGroupFees,
  adjustFee,
  filterFeesOutsideLimits,
  mapSubjectsToFees,
  normalizeFeeLimits,
} from './utils';

interface AdvisoryFeeCorrectionProps {
  isUnsavedChangesModalActive: boolean;
  hasAfterUnsavedChangesAction: boolean;
  onProceed: () => void;
  onReject: () => void;
}

const modalModeByInterceptorActionName: Partial<Record<StudioActionName, ModalMode>> = {
  onExcelExport: 'export',
  onPdfExport: 'saveOnPdfExport',
  onSave: 'save',
  onSaveAs: 'saveAs',
};

const AdvisoryFeeCorrectionInner = ({
  children,
  isUnsavedChangesModalActive,
  hasAfterUnsavedChangesAction,
  onProceed,
  onReject,
}: React.PropsWithChildren<AdvisoryFeeCorrectionProps>) => {
  const { isSaving, handleAfterUnsavedChangesAction } = useContext(StudioContext);

  const hasAdvisoryFeesForRlInvestmentsFF = useHasFF('advisory_fees_for_rl_investments_ff');

  const viewId = useRecoilValue(analysisViewIdState);
  const subjectInputIds = useRecoilValue(subjectInputGroups);
  const [subjectGroups, setSubjectGroups] = useRecoilState(multiSubjectInputGroupSubjects(subjectInputIds));

  const rawSubjects = useMemo(
    () =>
      Object.values(subjectGroups)
        .flatMap((groupSubject) => groupSubject)
        .filter(
          (rawSubject) => hasAdvisoryFeesForRlInvestmentsFF || rawSubject.portfolioId || rawSubject.privatePortfolioId,
        ),
    [hasAdvisoryFeesForRlInvestmentsFF, subjectGroups],
  );

  const subjects = useRecoilValue(analysisSubjects(rawSubjects));
  const subjectsWithoutHistorical = useMemo(
    () => subjects.filter((subject) => !subject.isHistoricalPortfolio),
    [subjects],
  );

  const rawWorkspaceFeeLimits = useCachedLoadableValue(workspaceFeeLimitsState, undefined);
  const workspaceFeeLimits = useMemo(
    () => (rawWorkspaceFeeLimits ? normalizeFeeLimits(rawWorkspaceFeeLimits) : undefined),
    [rawWorkspaceFeeLimits],
  );

  const subjectFees = useMemo<SubjectFee[]>(
    () => mapSubjectsToFees(rawSubjects, subjectsWithoutHistorical),
    [rawSubjects, subjectsWithoutHistorical],
  );

  const subjectFeesOutsideLimits = useMemo<SubjectFee[]>(
    () => (workspaceFeeLimits ? filterFeesOutsideLimits(subjectFees, workspaceFeeLimits) : []),
    [subjectFees, workspaceFeeLimits],
  );

  const subjectsToApplyCorrection = useMemo(
    () => new Set<string>(subjectFeesOutsideLimits.map(({ subjectId }) => String(subjectId))),
    [subjectFeesOutsideLimits],
  );

  const initialCheckDoneRef = useRef(false);
  const resolveInterceptorRef = useRef<((result: boolean) => void) | undefined>(undefined);

  const [modalMode, setModalMode] = useState<ModalMode | undefined>(undefined);

  useEffect(() => {
    initialCheckDoneRef.current = false;
  }, [viewId]);

  useEffect(() => {
    if (initialCheckDoneRef.current) {
      return;
    }

    if (workspaceFeeLimits && rawSubjects.length > 0) {
      initialCheckDoneRef.current = true;

      if (subjectFeesOutsideLimits.length) {
        setModalMode('info');
      }
    }
  }, [workspaceFeeLimits, rawSubjects, subjectFeesOutsideLimits]);

  // useLayoutEffect ensures setModalMode runs in the same render cycle (before paint),
  // preventing a frame where children render without the modal and causing a visible flicker.
  useLayoutEffect(() => {
    const shouldShowUnsavedChangesModal = isUnsavedChangesModalActive || hasAfterUnsavedChangesAction;
    if (shouldShowUnsavedChangesModal && subjectFeesOutsideLimits.length > 0) {
      setModalMode((current) => current ?? 'saveOnLeave');
    }
  }, [hasAfterUnsavedChangesAction, isUnsavedChangesModalActive, subjectFeesOutsideLimits]);

  const resolveInterceptor = useCallback((result: boolean) => {
    resolveInterceptorRef.current?.(result);
    resolveInterceptorRef.current = undefined;
  }, []);

  useRegisterStudioActionInterceptor(
    useCallback(
      async (actionName: StudioActionName) => {
        const modalMode = modalModeByInterceptorActionName[actionName];
        if (!modalMode || !subjectFeesOutsideLimits.length) {
          return Promise.resolve(true);
        }

        await new Promise((resolve) => {
          setTimeout(resolve, 0);
        });

        return new Promise((resolve) => {
          resolveInterceptorRef.current = resolve;
          setModalMode(modalMode);
        });
      },
      [subjectFeesOutsideLimits],
    ),
  );

  const onApply = useCallback(
    async (adjustType: FeeAdjustType) => {
      if (!workspaceFeeLimits) {
        return;
      }

      const updatedSubjectGroups = adjustSubjectGroupFees(subjectGroups, ({ subjectId, fee }) => {
        return subjectsToApplyCorrection.has(String(subjectId))
          ? adjustFee({ fee: fee ?? 0, adjustType, ...workspaceFeeLimits })
          : fee;
      });

      setSubjectGroups(updatedSubjectGroups);

      // After calling setSubjectGroups, we need to ensure that blocks are updated
      await new Promise((resolve) => {
        setTimeout(resolve, 0);
      });

      if (modalMode === 'saveOnLeave' || modalMode === 'saveOnPdfExport') {
        // Both modes replace ConfirmUnsavedChangesModal, so save must happen here rather than via resolveInterceptor()
        await handleAfterUnsavedChangesAction('saveAndProceed');
      }

      setModalMode(undefined);

      resolveInterceptor(true);

      if (isUnsavedChangesModalActive) {
        onProceed();
      }
    },
    [
      workspaceFeeLimits,
      subjectGroups,
      setSubjectGroups,
      modalMode,
      resolveInterceptor,
      isUnsavedChangesModalActive,
      subjectsToApplyCorrection,
      handleAfterUnsavedChangesAction,
      onProceed,
    ],
  );

  const onDiscard = useCallback(async () => {
    await handleAfterUnsavedChangesAction('proceed');

    resolveInterceptor(true);
    setModalMode(undefined);

    if (isUnsavedChangesModalActive) {
      onProceed();
    }
  }, [handleAfterUnsavedChangesAction, resolveInterceptor, isUnsavedChangesModalActive, onProceed]);

  const onClose = useCallback(async () => {
    resolveInterceptor(false);
    setModalMode(undefined);

    if (hasAfterUnsavedChangesAction) {
      await handleAfterUnsavedChangesAction('cancel');
    }

    if (isUnsavedChangesModalActive) {
      onReject();
    }
  }, [
    resolveInterceptor,
    hasAfterUnsavedChangesAction,
    isUnsavedChangesModalActive,
    handleAfterUnsavedChangesAction,
    onReject,
  ]);

  if (workspaceFeeLimits && modalMode) {
    return (
      <AdvisoryFeeCorrectionModal
        defaultFee={workspaceFeeLimits.defaultFee}
        minFee={workspaceFeeLimits.minFee}
        maxFee={workspaceFeeLimits.maxFee}
        subjectFees={subjectFeesOutsideLimits}
        actionsDisabled={isSaving}
        mode={modalMode}
        withDiscard={isUnsavedChangesModalActive}
        onApply={onApply}
        onClose={onClose}
        onDiscard={onDiscard}
      />
    );
  }

  return children;
};

const AdvisoryFeeCorrection = ({ children, ...props }: React.PropsWithChildren<AdvisoryFeeCorrectionProps>) => {
  const hasDefaultRlAdvisoryFeesFF = useHasFF('default_rl_advisory_fees_ff');
  const isPrinting = useRecoilValue(isPrintingState);
  const isReport = useRecoilValue(isReportState);

  if (!isReport || !hasDefaultRlAdvisoryFeesFF || isPrinting) {
    return children;
  }

  return <AdvisoryFeeCorrectionInner {...props}>{children}</AdvisoryFeeCorrectionInner>;
};

export default AdvisoryFeeCorrection;
